import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as libraryActions from '~/library/actions';
import Helpers from '~/utils/Helpers';
import SeverityFlag2 from '~/components/SeverityFlag2';
import { Link } from 'react-router';
import _ from 'lodash';
import moment from 'moment';
import { releaseStatusMap } from '~/constants/ModelConstants';
import PermissionsHelper from '~/utils/PermissionsHelper';
import CvssService from '~/utils/CvssService';
type LibraryViewSummaryBarProps = {
  libraryState: object;
  navigationState: object;
  myState: object;
  orgState: object;
  actions: object;
};
class LibraryViewSummaryBar extends React.Component<LibraryViewSummaryBarProps, {}> {
  constructor(props, context) {
    super(props, context);
  }
  componentDidMount() {
    const latestRelease = this.getLatestRelease();
    if (latestRelease && latestRelease.version === 'HEAD') {
      //In case a commit hash is the latest version, check if latest commit hash has vulnerabilities
      const latestCommit =
        latestRelease.latestCommitsPreview && latestRelease.latestCommitsPreview[0];
      this.props.actions.maybeLoadCommitHashVulnerabilities(latestCommit);
    }
  }
  getLatestRelease() {
    const { libraryState = {} } = this.props;
    const { component = {} } = libraryState;
    const { libraryModel = {} } = component;
    let { versions = [], latestRelease = {}, languageType } = libraryModel;
    if (!Helpers.isCommitHashSupportedByLanguage(languageType)) {
      return latestRelease;
    }
    const head = versions.find(version => version.version === 'HEAD');
    if (head) {
      if (
        head.releaseStatus &&
        head.releaseStatus.toLowerCase() === releaseStatusMap.RELEASE &&
        (!latestRelease || moment(head.latestRelease).isAfter(latestRelease.releaseDate))
      ) {
        return head;
      }
    }
    return latestRelease;
  }
  render() {
    const { libraryState = {}, myState, orgState } = this.props;
    const { component = {}, url, cvss } = libraryState;
    const { libraryModel = {}, stats = {} } = component;
    const {
      cvss2InstanceRiskCount = {},
      cvss3InstanceRiskCount = {},
      cvss2VulnCount = {},
      cvss3VulnCount = {},
      vulnCount: vulnCountAPIv1,
      noRiskInstanceCount,
      highRiskInstanceCount,
      mediumRiskInstanceCount,
      lowRiskInstanceCount,
      highRiskVulnCount,
    } = stats;
    const {
      languageType = '',
      versions: componentVersions = [],
      coordinateType = '',
      author = '',
      authorUrl,
    } = libraryModel;
    const latestRelease = this.getLatestRelease() || {};
    const latestVersion = latestRelease.version || 'n/a';
    const vulnCounts = latestRelease.vulnCounts || {};
    const versions =
      componentVersions && componentVersions.filter(version => version.version !== 'HEAD');
    const versionCount = versions ? versions.length : '--';
    const isLoggedInAndPaidOrTrialing = PermissionsHelper.isLoggedInAndPaidOrTrialing(
      myState,
      orgState
    );
    let latestVersionElement;
    let hasVulns = !!vulnCounts.high || !!vulnCounts.medium || !!vulnCounts.low;
    if (latestRelease.version) {
      if (latestVersion === 'HEAD') {
        const latestCommit =
          latestRelease &&
          latestRelease.latestCommitsPreview &&
          latestRelease.latestCommitsPreview[0];
        const { vulnerabilitiesByVersion = {} } = libraryState;
        const { [latestCommit]: latestCommitData = {} } = vulnerabilitiesByVersion;
        hasVulns = latestCommitData.length > 0;
        const latestCommitText = latestCommit && latestCommit.slice(0, 7);
        latestVersionElement = (
          <span>
            {isLoggedInAndPaidOrTrialing ? (
              <Link
                to={`${url}/commit_hash/${latestCommit}`}
                className="link--obvious hover--no-underline"
              >
                ({latestCommitText})
              </Link>
            ) : (
              latestCommitText
            )}
          </span>
        );
      } else {
        latestVersionElement = (
          <span>
            {isLoggedInAndPaidOrTrialing ? (
              <Link
                to={`${url}/versions/${latestVersion}`}
                className="link--obvious hover--no-underline"
              >
                {latestVersion}
              </Link>
            ) : (
              latestVersion
            )}
          </span>
        );
      }
    } else {
      latestVersionElement = <div>n/a</div>;
    }
    const latestReleaseDate = latestRelease?.releaseDate && new Date(latestRelease.releaseDate);
    const latestReleaseDateText = Helpers.timeSince(latestReleaseDate);
    const firstVersion = versions[versions.length - 1];
    const firstVersionDate = firstVersion?.releaseDate && new Date(firstVersion.releaseDate);
    const firstVersionDateText = firstVersionDate ? Helpers.timeSince(firstVersionDate) : '';
    const isCvss3Selected = cvss === 'cvss3' && CvssService.isCvss3Enabled();
    //Capture risk instance counts from v1 API for backwards compatibility use
    const isV2API =
      !_.isEmpty(cvss3InstanceRiskCount) ||
      !_.isEmpty(cvss2InstanceRiskCount) ||
      !_.isEmpty(cvss3VulnCount) ||
      !_.isEmpty(cvss2VulnCount);
    const instanceRiskCountAPIv1 = {
      high: highRiskInstanceCount,
      medium: mediumRiskInstanceCount,
      low: lowRiskInstanceCount,
      none: noRiskInstanceCount,
    };
    const vulnCountByRiskAPIv1 = {
      high: highRiskVulnCount,
    };
    //Stats: Versions Affected and Safe Versions count
    const instanceCountObj = isCvss3Selected
      ? cvss3InstanceRiskCount
      : isV2API
      ? cvss2InstanceRiskCount
      : instanceRiskCountAPIv1;
    const versionsAffectedCount = _.sum([
      instanceCountObj.critical,
      instanceCountObj.high,
      instanceCountObj.medium,
      instanceCountObj.low,
    ]);
    const safeVersionsCount = instanceCountObj.none;
    const vulnCountByRiskObj = isCvss3Selected
      ? cvss3VulnCount
      : isV2API
      ? cvss2VulnCount
      : vulnCountByRiskAPIv1;
    const criticalOrHighRiskVulnCount = isCvss3Selected
      ? _.sum([vulnCountByRiskObj.critical, vulnCountByRiskObj.high])
      : vulnCountByRiskObj.high;
    //Stats: Vulnerabilities count
    const vulnCount = isV2API
      ? _.sum([
          vulnCountByRiskObj.critical,
          vulnCountByRiskObj.high,
          vulnCountByRiskObj.medium,
          vulnCountByRiskObj.low,
        ])
      : vulnCountAPIv1;
    const maybeSeverityLevelCvss2 = !_.isEmpty(cvss2InstanceRiskCount)
      ? CvssService.getCvssSeverityByInstanceRiskCount(cvss2InstanceRiskCount)
      : CvssService.getCvssSeverityByInstanceRiskCount(instanceRiskCountAPIv1);
    const severityLevel = isCvss3Selected
      ? CvssService.getCvss3SeverityByInstanceRiskCount(cvss3InstanceRiskCount)
      : maybeSeverityLevelCvss2;
    const sanitizedPackageManagerType = Helpers.sanitizePackageManagerType(coordinateType);
    return (
      <div className="border-color--muted">
        <div className="grid grid--middle grid--full flex flex--align-items--flex-start bg-color--black-light bo-rad--bottom-5">
          <div className="grid__item col-1-4 ph pv bg-color--black-light color--white">
            <div className="grid">
              <div className="grid__item col-1-1 text--left">
                <SeverityFlag2 isCvss3={isCvss3Selected} severityLevel={severityLevel} />
                <div className="mt-">
                  <div className="font--14">
                    <strong>
                      {Helpers.sanitizeLanguageType(languageType)}{' '}
                      {sanitizedPackageManagerType && `(${sanitizedPackageManagerType})`}
                    </strong>
                  </div>
                  <div className="font--12 mt--">Packager</div>
                </div>
                {author && (
                  <div className="mt">
                    <div className="font--14">
                      <strong>
                        {authorUrl ? (
                          <Link
                            to={authorUrl}
                            className="link--obvious hover--no-underline"
                            target="_blank"
                          >
                            {author} <i className="fa fa-external-link font--12" />
                          </Link>
                        ) : (
                          author
                        )}
                      </strong>
                    </div>
                    <div className="font--12 mt--">Author</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid__item col-3-4 ph pt lh+ bg-color--white-light bo-rb--1 border-color--white-dark min-height--190">
            <div className="grid">
              <div className="grid__item col-1-2 ph">
                <div className="grid">
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18 text--ellipsis" title={latestVersion}>
                      <strong>{latestVersionElement}</strong>
                    </div>
                    <div className="font--14 mt--">
                      Latest version
                      {`${
                        Helpers.isCommitHashSupportedByLanguage(languageType)
                          ? ' or commit hash'
                          : ''
                      }`}
                    </div>
                    {latestReleaseDateText && <div>released {latestReleaseDateText}</div>}
                  </div>
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18">
                      <strong>{versionCount}</strong>
                    </div>
                    <div className="font--14 mt--">Versions</div>
                    {firstVersionDateText && <div>since {firstVersionDateText}</div>}
                  </div>
                  <div className="grid__item col-1-1 mt">
                    Latest has vulnerabilities?{' '}
                    <span
                      className={`${hasVulns ? 'color--danger' : 'color--success-dark'} text--bold`}
                    >
                      {hasVulns ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid__item col-1-2 ph bo-l--2 border-color--muted-light">
                <div className="grid">
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18">
                      <strong>{vulnCount}</strong>
                    </div>
                    <div className="font--14 mt-- pb- mb-">Vulnerabilities</div>
                  </div>
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18">
                      <strong>{criticalOrHighRiskVulnCount}</strong>
                    </div>
                    <div className="font--14 mt-- pb- mb-">
                      {isCvss3Selected ? 'Critical/High' : 'High'} Risk Vulns
                    </div>
                  </div>
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18">
                      <strong>{versionsAffectedCount}</strong>
                    </div>
                    <div className="font--14 mt--">Versions Affected</div>
                  </div>
                  <div className="grid__item col-1-2 text--left color--black-light">
                    <div className="font--18">
                      <strong>{safeVersionsCount}</strong>
                    </div>
                    <div className="font--14 mt--">Safe Versions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
function mapStateToProps(state) {
  return {
    navigationState: state.navigationState,
    libraryState: state.libraryState,
    myState: state.myState,
    orgState: state.orgState,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(libraryActions, dispatch),
  };
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LibraryViewSummaryBar);
